import {
  createContext,
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react"
import { useLocation, useNavigate } from "react-router"
import { get as dig } from "lodash"
import PropTypes from "prop-types"
import {
  removeLocalStorageValue,
  useLocalStorage,
} from "source/shared/hooks/useLocalStorage"
import { useWebBootQuery } from "source/publishing/WebBoot/useWebBootQuery"
import {
  clearPreviousLocalWebBootState,
  resetLocalWebBootState,
} from "source/publishing/WebBoot/webBootState"
import ThemeStyles from "./ThemeStyles"

const NOOP = () => null

export const DRAFT_MODE_KEY = "WebBoot.draftMode"

export const WebBootContext = createContext()

const resetCacheRef = createRef()
resetCacheRef.current = NOOP

export function resetCache() {
  resetCacheRef.current()
}

WebBoot.propTypes = {
  initialState: PropTypes.object,
  initialStateIsFresh: PropTypes.bool,
  children: PropTypes.node.isRequired,
}
export function WebBoot({ initialState, initialStateIsFresh, children }) {
  const navigate = useNavigate()
  const { search } = useLocation()
  const { webBootData: state, refetchWebBoot } = useWebBootQuery(
    initialState,
    initialStateIsFresh,
  )
  const [draftMode, setDraftMode] = useLocalStorage(DRAFT_MODE_KEY, false)
  const mode = draftMode ? "draft" : "published"
  const draftModeRequested = useRef(search.includes("start_draft_mode=1"))

  const canDraftMode = dig(state, "abilities.publishing.draft_mode") || false

  useEffect(() => {
    if (draftModeRequested.current && canDraftMode) {
      setDraftMode(true)
      draftModeRequested.current = false
    }
  }, [canDraftMode])

  // Make setDraftMode available to React Native
  const inReactNativeWebView = !!window.ReactNativeWebView
  useEffect(() => {
    if (inReactNativeWebView) window.setDraftMode = setDraftMode
  }, [inReactNativeWebView, setDraftMode])

  useEffect(() => {
    if (search.includes("start_draft_mode=1")) {
      let nextUrl = new URL(window.location.toString())
      nextUrl.searchParams.delete("start_draft_mode")
      nextUrl = nextUrl.toString().substring(nextUrl.origin.length) // relative url

      navigate(nextUrl.toString(), {
        state: { ...history.state },
        replace: true,
      })
    }
  }, [draftMode])

  useEffect(() => {
    clearPreviousLocalWebBootState()
  }, [])

  const resetCache = useCallback(() => {
    resetLocalWebBootState()
    removeLocalStorageValue(DRAFT_MODE_KEY)
  }, [])

  useEffect(() => {
    resetCacheRef.current = resetCache
    return () => (resetCacheRef.current = NOOP)
  }, [resetCache])

  const reactToCandidateEvents = useCallback(
    (event) => {
      if (isPageEventForNonHomePage(event)) return

      refetchWebBoot()
    },
    [refetchWebBoot],
  )

  const contextValue = useMemo(
    () => ({
      ...state,
      draftMode,
      reactToCandidateEvents,
      setDraftMode,
    }),
    [state, draftMode, reactToCandidateEvents, setDraftMode],
  )

  return (
    <WebBootContext.Provider value={contextValue}>
      <ThemeStyles mode={mode} />
      {children}
    </WebBootContext.Provider>
  )
}

const isPageEventForNonHomePage = (event) => {
  return (
    dig(event, "type") === "Page" &&
    dig(event, "relationships.slug.data.id") !== "home"
  )
}
